<template>
    <div className="edit-quill" id="editor" style="height: 100%;">
        <QuillEditor
            ref="editor"
            theme="bubble"
            placeholder="Вставьте сюда текст перед&nbsp;публикацией и&nbsp;посмотрите, на&nbsp;что&nbsp;форма&nbsp;обратит внимание"
            :options="options"
            @textChange="textChange"
            @click="errorClick"
            @selectionChange="selectionChange"
            @paste="pasteTextFromClipboard"
        />
    </div>
</template>

<script>
import {QuillEditor} from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.bubble.css'
import Delta from "quill-delta"
import {buildCpToUtf16Map} from '../store/index'
import {normalizeClipboardHtml, copyHtmlToClipboard, verifyCurrentClipboardFormats} from '../utils/clipboardHtml'
export default {
    components: {
        QuillEditor
    },
    name: 'TextArea',
    data() {
        return {
            options: {
                modules: {
                    keyboard: {
                        bindings: {
                            'list autofill': {
                                prefix: /^\s{0,}(1){1,1}(\.|-|\*|\[ ?\]|\[x\])$/
                            }
                        }
                    }
                }
            }
        }
    },
    mounted() {
        this.$eventHub.on('clickOnTheFixButton', (tip) => {
            this.fixIssue(tip)
        })
        this.$eventHub.on('clickOnTheFixAllButton', (tip) => {
            this.fixIssues(tip)
        })
        this.$eventHub.on('mouseClickOnSelectedText', () => {
            setTimeout(() => {
                this.dropIssuesMarks()
                this.markIssues()
            }, 100)
        })
        this.$eventHub.on('hoveringTextCursorOverSelectedText', () => {
            setTimeout(() => {
                this.dropIssuesMarks()
                this.markIssues()
            }, 100)
        })
        this.$eventHub.on('clickOnTheClipboardButton', () => {
            this.copyTextToClipboard()
        })
        this.$nextTick(() => {
            const quill = this.$refs.editor?.getQuill()
            if (quill && quill.root) {
                quill.root.addEventListener('click', this.handleEditorLinkClick)
                this.stripLinkTitles(quill.root)
            }
        })
    },
    beforeUnmount() {
        const quill = this.$refs.editor?.getQuill()
        if (quill && quill.root) {
            quill.root.removeEventListener('click', this.handleEditorLinkClick)
        }
    },
    methods: {
        findQuillCoordinates() {
            let elem = document.querySelector(".ql-editor");
            return elem.getBoundingClientRect();
        },
        errorClick(event) {
            this.$eventHub.emit('hideTips');
            let x = event.clientX
            let y = event.clientY
            this.$store.getters.bounds.forEach((el) => {
                if (el.left < x && x < el.right && el.top < y && y < el.bottom) {
                    this.$eventHub.emit('mouseClickOnSelectedText', el.id);
                }
            })
        },
        toRoman(num, upper = true) {
            if (num <= 0) return String(num);
            const map = [
                [1000,'M'],[900,'CM'],[500,'D'],[400,'CD'],
                [100,'C'],[90,'XC'],[50,'L'],[40,'XL'],
                [10,'X'],[9,'IX'],[5,'V'],[4,'IV'],[1,'I']
            ];
            let res = '';
            for (const [v, s] of map) {
                while (num >= v) { res += s; num -= v; }
            }
            return upper ? res : res.toLowerCase();
        },
        toAlpha(num, upper = false) {
            // 1 -> a, 26 -> z, 27 -> aa
            let n = Math.max(1, num);
            let s = '';
            while (n > 0) {
                n--; s = String.fromCharCode(97 + (n % 26)) + s; n = Math.floor(n / 26);
            }
            return upper ? s.toUpperCase() : s;
        },
        parseListStyleTypeFromStyleAttr(el) {
            const style = el.getAttribute && el.getAttribute('style');
            if (!style) return null;
            const m = style.match(/list-style-type\s*:\s*([a-z-]+)/i);
            return m ? m[1].toLowerCase() : null;
        },
        bulletForUl(el) {
            const t = this.parseListStyleTypeFromStyleAttr(el);
            switch (t) {
                case 'circle': return '○';
                case 'square': return '▪';
                // disc, decimal, etc -> default
                default: return '•';
            }
        },
        olType(el) {
            console.log(el)
            const t = (el.getAttribute && el.getAttribute('type')) || '';
            if (['1','a','A','i','I'].includes(t)) return t;
            return '1';
        },
        formatOlIndex(n, type) {
            switch (type) {
                case 'a': return this.toAlpha(n, false) + '.';
                case 'A': return this.toAlpha(n, true) + '.';
                case 'i': return this.toRoman(n, false) + '.';
                case 'I': return this.toRoman(n, true) + '.';
                default: return String(n) + '.';
            }
        },
        getTextWithLinks(quill) {
            const delta = quill.getContents()
            let result = ''
            for (const op of delta.ops) {
                const text = typeof op.insert === 'string' ? op.insert : ''
                if (op.attributes && op.attributes.link) {
                    const href = op.attributes.link
                    const safeHref = href.split('&').join('&amp;').split('"').join('&quot;')
                    result += '<a href="' + safeHref + '">' + text + '</a>'
                } else {
                    result += text
                }
            }
            return result
        },
        /**
         * Текст со ссылками в формате Markdown [текст](url) — Telegram при вставке превращает в кликабельную ссылку.
         */
        getPlainTextWithMarkdownLinks(quill) {
            const delta = quill.getContents()
            let result = ''
            for (const op of delta.ops) {
                const text = typeof op.insert === 'string' ? op.insert : ''
                if (op.attributes && op.attributes.link) {
                    const href = op.attributes.link
                    if (href) {
                        const label = text.replace(/\]/g, '\\]')
                        result += '[' + label + '](' + href + ')'
                    } else {
                        result += text
                    }
                } else {
                    result += text
                }
            }
            return result
        },
        /**
         * Извлекает значение href из строки открывающего тега <a ...> без innerHTML,
         * чтобы браузер не нормализовал/не обрезал URL (например относительный путь wiki/...).
         */
        getHrefFromTagString(tagStr) {
            const lower = tagStr.toLowerCase()
            const hrefIdx = lower.indexOf('href')
            if (hrefIdx === -1) return null
            const eqIdx = tagStr.indexOf('=', hrefIdx + 4)
            if (eqIdx === -1) return null
            let pos = eqIdx + 1
            while (pos < tagStr.length && (tagStr[pos] === ' ' || tagStr[pos] === '\t')) pos++
            if (pos >= tagStr.length) return null
            const q = tagStr[pos]
            if (q !== '"' && q !== "'") return null
            const start = pos + 1
            pos = start
            while (pos < tagStr.length) {
                if (tagStr[pos] === '\\' && pos + 1 < tagStr.length) {
                    pos += 2
                    continue
                }
                if (tagStr[pos] === q) return tagStr.substring(start, pos)
                pos++
            }
            return null
        },
        /**
         * Находит позицию '>', закрывающую открывающий тег (игнорирует '>' внутри значений атрибутов в кавычках).
         */
        findOpeningTagEnd(str, start) {
            let inQuote = null
            for (let j = start; j < str.length; j++) {
                const c = str[j]
                if (inQuote) {
                    if (c === inQuote && str[j - 1] !== '\\') inQuote = null
                    continue
                }
                if (c === '"' || c === "'") { inQuote = c; continue }
                if (c === '>') return j
            }
            return -1
        },
        parseTextWithLinks(textWithLinks) {
            if (!textWithLinks) return { text: '', links: [] }
            if (textWithLinks.indexOf('<') === -1) return { text: textWithLinks, links: [] }

            const links = []
            let plainText = ''
            let i = 0
            const len = textWithLinks.length
            const lower = textWithLinks.toLowerCase()

            while (i < len) {
                if (textWithLinks[i] === '<') {
                    if (lower.startsWith('<a ', i) || lower.startsWith('<a>', i)) {
                        const tagEnd = this.findOpeningTagEnd(textWithLinks, i)
                        if (tagEnd !== -1) {
                            const tagStr = textWithLinks.substring(i, tagEnd + 1)
                            let href = this.getHrefFromTagString(tagStr)
                            if (href != null && typeof href === 'string') {
                                href = href.trim()
                            }
                            if (href === '') href = null

                            const closeStart = lower.indexOf('</a', tagEnd + 1)
                            if (closeStart !== -1) {
                                const closeEnd = textWithLinks.indexOf('>', closeStart)
                                if (closeEnd !== -1) {
                                    const innerText = textWithLinks.substring(tagEnd + 1, closeStart)
                                    const start = plainText.length
                                    plainText += innerText
                                    if (href) {
                                        links.push({ start, end: plainText.length, href })
                                    }
                                    i = closeEnd + 1
                                    continue
                                }
                            }
                        }
                    }
                    if (lower.startsWith('</a>', i) || lower.startsWith('</a ', i)) {
                        const tagEnd = textWithLinks.indexOf('>', i)
                        if (tagEnd !== -1) { i = tagEnd + 1; continue }
                    }
                }
                plainText += textWithLinks[i]
                i++
            }

            return { text: plainText, links }
        },
        /**
         * Извлекает exact href первой ссылки из сырой строки clipboard HTML (без парсера, чтобы не менять query/anchor).
         */
        getFirstLinkHrefFromClipboardHtml(html) {
            if (!html || typeof html !== 'string') return null
            const lower = html.toLowerCase()
            let start = lower.indexOf('<a ')
            if (start === -1) start = lower.indexOf('<a>')
            if (start === -1) return null
            const tagEnd = this.findOpeningTagEnd(html, start)
            if (tagEnd === -1) return null
            return this.getHrefFromTagString(html.substring(start, tagEnd + 1))
        },
        /**
         * Пытается извлечь base URL из строки clipboard HTML.
         * Проверяет: (1) заголовок в стиле CF_HTML с SourceURL: (2) тег <base href="...">.
         * Возвращает абсолютный URL или null.
         */
        getBaseUrlFromClipboardHtml(html) {
            if (!html || typeof html !== 'string') return null
            const s = html.trim()
            const headerZone = s.substring(0, 1500)
            const sourceUrlMatch = headerZone.match(/\bSourceURL:\s*(\S+)/im)
            if (sourceUrlMatch) {
                const url = sourceUrlMatch[1].trim()
                try {
                    const u = new URL(url)
                    if (/^https?:$/i.test(u.protocol)) return u.origin
                } catch (_) { /* invalid URL */ }
            }
            const baseMatch = s.match(/<base\s+[^>]*href\s*=\s*["']([^"']+)["']/i)
            if (baseMatch) {
                try {
                    const u = new URL(baseMatch[1].trim())
                    if (/^https?:$/i.test(u.protocol)) return u.origin
                } catch (_) { /* invalid URL */ }
            }
            return null
        },
        /**
         * Quill принимает только http/https/mailto/tel; относительные подменяет на about:blank.
         * Абсолютные возвращаем как есть. Относительные: если передан baseUrl (из буфера), используем его, иначе текущий origin.
         */
        ensureLinkHref(href, baseUrl) {
            if (!href || typeof href !== 'string') return null
            const t = href.trim()
            if (!t) return null
            if (/^(https?:|mailto:|tel:)/i.test(t)) return t
            const base = (baseUrl && typeof baseUrl === 'string') ? baseUrl : window.location.origin
            try {
                return new URL(t, base).href
            } catch (_) {
                return t
            }
        },
        setContentFromHtmlText(quill, htmlText) {
            const { text, links } = this.parseTextWithLinks(htmlText)
            if (links.length > 0) {
                console.log('[href-trace] SETCONTENT 3. parseTextWithLinks(htmlText) => links[0].href =', JSON.stringify(links[0].href))
            }
            if (links.length > 0 && links[0].href) {
                const applied = this.ensureLinkHref(links[0].href)
                console.log('[href-trace] SETCONTENT 4. ensureLinkHref(input) =>', JSON.stringify(applied))
                console.log('[href-trace] SETCONTENT 5. quill.formatText(..., { link: above })')
            }
            quill.setText(text, 'silent')
            for (const link of links) {
                const href = this.ensureLinkHref(link.href)
                if (href) {
                    quill.formatText(link.start, link.end - link.start, { link: href }, 'silent')
                }
            }
            if (links.length > 0) {
                this.$nextTick(() => {
                    const root = quill.root
                    if (root) this.stripLinkTitles(root)
                    const a = root ? root.querySelector('a') : null
                    const domHref = a ? a.getAttribute('href') : null
                    console.log('[href-trace] SETCONTENT 6. DOM <a>.getAttribute("href") =', JSON.stringify(domHref))
                })
            }
        },
        /** Убираем title у ссылок в редакторе, чтобы не показывался браузерный tooltip. */
        stripLinkTitles(editorRoot) {
            if (!editorRoot || typeof editorRoot.querySelectorAll !== 'function') return
            editorRoot.querySelectorAll('a[title]').forEach(a => {
                a.removeAttribute('title')
            })
        },
        htmlToStructuredText(html, opts = {}) {
            const LINK_START = '\uFDD0';
            const LINK_SEP = '\uFDD2';
            const LINK_END = '\uFDD1';

            const vm = this;
            const options = {
                indent: '  ',
                table: 'tsv', // 'tsv' | 'text' | 'ignore'
                ...opts
            };
            const parser = new DOMParser();
            // Некоторые источники кладут фрагменты; HTML-парсер обернет их в документ
            const doc = parser.parseFromString(html, 'text/html');
            const root = doc.body || doc.documentElement;

            const out = [];
            const EOL = '\n';

            const ctx = {
                listStack: [], // элементы: {kind:'ul'|'ol', bullet?, type?, reversed?, current?}
                preserveWS: false,
                atLineStart: true,
                lastChar() {
                    if (!out.length) return '';
                    const s = out[out.length - 1];
                    return s ? s[s.length - 1] : '';
                },
                push(s) {
                    if (!s) return;
                    out.push(s);
                    this.atLineStart = s.endsWith('\n');
                },
                ensureBreak() {
                    if (this.lastChar() !== '\n') this.push(EOL);
                },
                ensureBlankLine() {
                    // минимум один перенос; если уже есть один — не делаем двойной
                    if (this.lastChar() !== '\n') this.push(EOL);
                },
                pushCollapsed(text) {
                    if (!text) return;
                    if (this.preserveWS) {
                        this.push(text);
                        return;
                    }
                    // Коллапсим только обычные пробелы/табы/переносы, NBSP (U+00A0) не трогаем
                    let t = text.replace(/[ \t\r\n\f]+/g, ' ');
                    // если в начале новой строки — не начинаем с пробела
                    if (this.atLineStart) t = t.replace(/^ +/, '');
                    this.push(t);
                }
            };

            const BLOCK_BREAK = new Set([
                'ADDRESS','ARTICLE','ASIDE','BLOCKQUOTE','DIV','DL','FIELDSET','FIGCAPTION','FIGURE',
                'FOOTER','FORM','H1','H2','H3','H4','H5','H6','HEADER','HR','MAIN','NAV','OL','P',
                'PRE','SECTION','UL'
            ]);

            function visit(node) {
                switch (node.nodeType) {
                    case Node.ELEMENT_NODE: {
                        const tag = node.tagName;
                        if (tag === 'BR') {
                            ctx.push('\n');
                            return;
                        }
                        if (tag === 'PRE' || tag === 'CODE' || tag === 'SAMP' || tag === 'KBD') {
                            const prev = ctx.preserveWS; ctx.preserveWS = true;
                            // Отдельной строкой
                            if (!ctx.atLineStart) ctx.ensureBreak();
                            node.childNodes.forEach(visit);
                            ctx.ensureBreak();
                            ctx.preserveWS = prev;
                            return;
                        }
                        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') {
                            return;
                        }
                        if (tag === 'UL') {
                            // Новый уровень списка
                            ctx.listStack.push({ kind: 'ul', bullet: vm.bulletForUl(node) });
                            // Перенос перед списком, если нужно
                            if (!ctx.atLineStart) ctx.ensureBreak();
                            node.childNodes.forEach(visit);
                            // После списка — перенос
                            ctx.ensureBreak();
                            ctx.listStack.pop();
                            return;
                        }
                        if (tag === 'OL') {
                            const t = vm.olType(node);
                            const reversed = node.hasAttribute('reversed');
                            const startAttr = node.getAttribute('start');
                            const start = startAttr ? parseInt(startAttr, 10) || 1 : 1;
                            const current = reversed ? start : start;
                            ctx.listStack.push({ kind: 'ol', type: t, reversed, current, start });
                            if (!ctx.atLineStart) ctx.ensureBreak();
                            node.childNodes.forEach(visit);
                            ctx.ensureBreak();
                            ctx.listStack.pop();
                            return;
                        }
                        if (tag === 'LI') {
                            // Маркер строки списка
                            ctx.ensureBreak();
                            const indent = options.indent.repeat(Math.max(0, ctx.listStack.length - 1));
                            ctx.push(indent);
                            const level = ctx.listStack[ctx.listStack.length - 1];
                            let marker = '-';
                            if (!level || level.kind === 'ul') {
                                const b = level ? level.bullet : '•';
                                marker = b;
                            } else {
                                // ol
                                let val;
                                const valAttr = node.getAttribute('value');
                                if (valAttr != null) {
                                    val = parseInt(valAttr, 10) || 0;
                                    // Синхронизируем счетчик для последующих пунктов
                                    level.current = val;
                                } else {
                                    val = level.current;
                                }
                                marker = vm.formatOlIndex(val, level.type);
                                // Обновим счетчик
                                if (valAttr == null) {
                                    level.current += (level.reversed ? -1 : 1);
                                } else {
                                    // если текущий li задал value — следующий будет +/- 1 от него
                                    level.current = val + (level.reversed ? -1 : 1);
                                }
                            }
                            ctx.push(marker + ' ');
                            node.childNodes.forEach(visit);
                            ctx.ensureBreak();
                            return;
                        }
                        if (tag === 'TABLE') {
                            if (options.table === 'ignore') {
                                // Пропускаем как блочный элемент
                                ctx.ensureBreak();
                                return;
                            }
                            if (!ctx.atLineStart) ctx.ensureBreak();
                            const rows = Array.from(node.querySelectorAll('tr'));
                            for (let r = 0; r < rows.length; r++) {
                                const cells = Array.from(rows[r].children).filter(c => c.tagName === 'TD' || c.tagName === 'TH');
                                const parts = cells.map(c => {
                                    // Вытянем текст по тем же правилам, но локально
                                    const tmpCtx = { ...ctx };
                                    const buf = [];
                                    const savePush = tmpCtx.push, saveAtLineStart = tmpCtx.atLineStart;
                                    console.log(savePush + ' ' + saveAtLineStart)
                                    tmpCtx.push = (s) => buf.push(s);
                                    tmpCtx.atLineStart = true;
                                    c.childNodes.forEach(visit.bind(null));
                                    // Собираем
                                    let s = buf.join('');
                                    s = s.replace(/\n+/g, ' ').trim();
                                    return s;
                                });
                                ctx.push(parts.join(options.table === 'tsv' ? '\t' : ' | '));
                                ctx.ensureBreak();
                            }
                            return;
                        }
                        if (tag === 'A') {
                            const href = node.getAttribute('href');
                            if (href) ctx.push(LINK_START + href + LINK_SEP);
                            node.childNodes.forEach(visit);
                            if (href) ctx.push(LINK_END);
                            return;
                        }
                        if (tag === 'BR') {
                            ctx.push('\n'); return;
                        }
                        if (BLOCK_BREAK.has(tag)) {
                            if (!ctx.atLineStart) ctx.ensureBreak();
                            node.childNodes.forEach(visit);
                            ctx.ensureBreak();
                            return;
                        }
                        // По умолчанию — просто пройтись по детям
                        node.childNodes.forEach(visit);
                        return;
                    }
                    case Node.TEXT_NODE: {
                        const t = node.nodeValue || '';
                        // NBSP (u00A0), узкие неразрывные (u202F) и т.п. остаются как есть
                        ctx.pushCollapsed(t);
                        return;
                    }
                    default:
                        return;
                }
            }

            visit(root);

            // Финальная нормализация: уберем хвостовые пустые строки
            let result = out.join('');
            result = result.replace(/[ \t]+\n/g, '\n');
            result = result.replace(/\n{3,}/g, '\n\n');
            result = result.replace(/^\s+|\s+$/g, '');

            const links = [];
            let cleanText = '';
            let idx = 0;
            while (idx < result.length) {
                if (result[idx] === LINK_START) {
                    const sepPos = result.indexOf(LINK_SEP, idx + 1);
                    if (sepPos === -1) { cleanText += result[idx]; idx++; continue; }
                    const href = result.substring(idx + 1, sepPos);
                    const endPos = result.indexOf(LINK_END, sepPos + 1);
                    if (endPos === -1) { idx = sepPos + 1; continue; }
                    const linkText = result.substring(sepPos + 1, endPos);
                    const start = cleanText.length;
                    cleanText += linkText;
                    links.push({ start, end: cleanText.length, href });
                    idx = endPos + 1;
                } else if (result[idx] === LINK_END || result[idx] === LINK_SEP) {
                    idx++;
                } else {
                    cleanText += result[idx];
                    idx++;
                }
            }

            return { text: cleanText, links };
        },
        async pasteTextFromClipboard(clipboardEvent) {
            clipboardEvent.preventDefault()
            const quill = this.$refs.editor.getQuill()
            let currText = quill.getText()
            let clipboardHtml = clipboardEvent.clipboardData.getData('text/html')
            let clipboardText = clipboardEvent.clipboardData.getData('text/plain')
            let pasteLinks = []
            let detectedBase = null
            const clipboardRaw = clipboardHtml
            if (clipboardHtml.length !== 0) {
                detectedBase = this.getBaseUrlFromClipboardHtml(clipboardRaw)

                const parsed = this.htmlToStructuredText(clipboardHtml)
                clipboardText = parsed.text
                pasteLinks = parsed.links

                if (pasteLinks.length > 0 && pasteLinks[0].href) {
                    const step1 = this.getFirstLinkHrefFromClipboardHtml(clipboardRaw)
                    const step2 = pasteLinks[0].href
                    const step3 = this.ensureLinkHref(pasteLinks[0].href, detectedBase)
                    console.log('[href-trace] 1. exact href from clipboard HTML (raw) =', JSON.stringify(step1))
                    console.log('[href-trace] 2. exact href after htmlToStructuredText() =', JSON.stringify(step2))
                    console.log('[href-trace] 3. exact href after ensureLinkHref() =', JSON.stringify(step3))
                    console.log('[href-trace] 4. exact href to quill.formatText(..., { link }) =', JSON.stringify(step3))
                }
            }
            let prevIndex = 0
            let range = quill.getSelection(true)
            if (range) {
                prevIndex = range.index
                let head = currText.substring(0, range.index)
                let tail = currText.substring(range.index + range.length, currText.length - 1)
                await quill.deleteText(0, quill.getLength())
                setTimeout(() => {
                    quill.insertText(0, head + clipboardText + tail, 'user')
                    for (const link of pasteLinks) {
                        const href = this.ensureLinkHref(link.href, detectedBase)
                        if (href) {
                            quill.formatText(
                                head.length + link.start,
                                link.end - link.start,
                                { link: href },
                                'silent'
                            )
                        }
                    }
                    if (quill.root) this.stripLinkTitles(quill.root)
                    if (pasteLinks.length > 0 && pasteLinks[0].href) {
                        const a = quill.root ? quill.root.querySelector('a') : null
                        const step5 = a ? a.getAttribute('href') : null
                        console.log('[href-trace] 5. exact href in DOM after render =', JSON.stringify(step5))
                        const step1 = this.getFirstLinkHrefFromClipboardHtml(clipboardRaw)
                        const step3 = this.ensureLinkHref(pasteLinks[0].href, detectedBase)
                        const ok = step1 === step5 && step3 === step5
                        console.log('[href-trace] ABSOLUTE URL PASS: pipeline unchanged?', ok, ok ? '' : '(step1===step5 && step3===step5)')
                    }
                }, 5)
            } else {
                await quill.insertText(0, '\n', 'user')
            }
            quill.update('user')
            setTimeout(() => quill.setSelection(prevIndex + clipboardText.length, 0, 'user'), 5)
            await this.refreshTextArea()
        },
        selectionChange(range) {
            if (range.source === 'user' && !!range.range && !!range.range.index) {
                let index = range.range.index;
                this.$store.getters.tips.forEach((el) => {
                    if (index >= el.index && index < el.index + el.size) {
                        this.$eventHub.emit('hoveringTextCursorOverSelectedText', el.id);
                    }
                })
            }
        },
        /**
         * Применить единичный Fix через бэкенд.
         *
         * Координаты:
         *   tip.cpIndex / tip.cpSize — code points (бэкенд, PHP mb_strlen)
         *   tip.index   / tip.size   — UTF-16 code units (Quill, JS .length)
         *
         * Бэкенд:
         *   1) mb_substr заменяет [cpIndex .. cpIndex+cpSize) на replacement
         *   2) пересчитывает все issues на новом тексте
         *   3) возвращает {text, issues} — code-point координаты
         *
         * Фронт:
         *   1) подменяет весь текст в Quill
         *   2) конвертирует code-point координаты → UTF-16
         *   3) отрисовывает подсветку
         */
        async fixIssue(tip) {
            this.dropIssuesMarks()
            const quill = this.$refs.editor.getQuill();
            const currentText = this.getTextWithLinks(quill);

            try {
                const result = await this.$store.dispatch('fixTip', {text: currentText, tip});

                if (!result || !result.text || !Array.isArray(result.issues)) {
                    console.error('fixIssue: неверный формат ответа от бэкенда', result);
                    await this.refreshTips();
                    this.markIssues();
                    return;
                }

                this.setContentFromHtmlText(quill, result.text);
                quill.update();

                // Ждем обновления DOM перед загрузкой issues для корректной работы с эмодзи
                await this.$nextTick();
                // Загружаем новые issues и СРАЗУ обновляем подсветку
                console.log('fixIssue: получено issues от бэкенда:', result.issues.length, result.issues);
                this.loadIssuesFromBackend(result.text, result.issues);
                console.log('fixIssue: после loadIssuesFromBackend, tips в store:', this.$store.getters.tips.length);
                this.markIssues();
            } catch (e) {
                console.error('fixIssue error:', e);
                await this.refreshTips();
                this.markIssues();
            }

            this.$store.dispatch('dropActiveTip')
        },
        /**
         * Применить Fix All (все issues с тем же mark) через бэкенд.
         */
        async fixIssues(tip) {
            this.dropIssuesMarks()
            const quill = this.$refs.editor.getQuill();
            const currentText = this.getTextWithLinks(quill);
            const tipsWithSameMark = this.$store.getters.tips.filter(
                t => t.mark === tip.mark && t.correct !== false && Array.isArray(t.correct)
            )
            const fixes = tipsWithSameMark.map(t => ({
                cp_index: t.cpIndex,
                cp_size: t.cpSize,
                replacement: t.correct.join(''),
            }))

            try {
                const result = await this.$store.dispatch('fixAllTips', {
                    text: currentText,
                    fixes,
                });

                if (!result || !result.text || !Array.isArray(result.issues)) {
                    console.error('fixIssues: неверный формат ответа от бэкенда', result);
                    await this.refreshTips();
                    this.markIssues();
                    return;
                }

                this.setContentFromHtmlText(quill, result.text);
                quill.update();

                // Ждем обновления DOM перед загрузкой issues для корректной работы с эмодзи
                await this.$nextTick();
                // Загружаем новые issues и СРАЗУ обновляем подсветку
                console.log('fixIssues: получено issues от бэкенда:', result.issues.length, result.issues);
                this.loadIssuesFromBackend(result.text, result.issues);
                console.log('fixIssues: после loadIssuesFromBackend, tips в store:', this.$store.getters.tips.length);
                this.markIssues();
            } catch (e) {
                console.error('fixIssues error:', e);
                await this.refreshTips();
                this.markIssues();
            }

            this.$store.dispatch('dropActiveTip')
        },
        /**
         * Загрузить issues из ответа бэкенда (без повторного запроса).
         * Конвертирует code-point позиции → UTF-16 для Quill.
         */
        loadIssuesFromBackend(text, issues) {
            console.log('loadIssuesFromBackend: получено', issues.length, 'issues');
            const quill = this.$refs.editor.getQuill();
            const plainText = quill.getText();
            const cpToUtf16 = buildCpToUtf16Map(plainText);
            const tips = [];
            for (let i = 0; i < issues.length; i++) {
                const d = issues[i];
                const utf16Start = cpToUtf16(d.index);
                const utf16End = cpToUtf16(d.index + d.size);
                tips.push({
                    id: d.id,
                    mark: d.mark,
                    title: d.title,
                    description: d.description,
                    cpIndex: d.index,
                    cpSize: d.size,
                    index: utf16Start,
                    size: utf16End - utf16Start,
                    correct: d.correct,
                    top: 0,
                    position: 0,
                    active: false,
                });
            }
            console.log('loadIssuesFromBackend: создано', tips.length, 'tips:', tips.map(t => t.title));
            this.$store.dispatch('setLocalTips', tips);
            const quillLength = quill.getLength() - 1; // Quill добавляет \n в конец
            const rect = this.findQuillCoordinates();
            this.$store.dispatch('clearBounds');
            tips.forEach((el) => {
                // Проверяем границы перед вызовом getBounds
                const start = Math.max(0, Math.min(el.index, quillLength));
                const end = Math.max(start, Math.min(el.index + el.size, quillLength));
                const size = end - start;
                
                if (size > 0) {
                    let bounds = quill.getBounds(start, size);
                    if (bounds) {
                        bounds.inner_top = bounds.top;
                        bounds.top += rect.top;
                        bounds.left += rect.left;
                        bounds.right = bounds.left + bounds.width;
                        bounds.bottom = bounds.top + bounds.height;
                        bounds.id = el.id;
                        this.$store.dispatch('addBounds', bounds);
                    }
                }
            });
        },
        dropIssuesMarks() {
            const quill = this.$refs.editor.getQuill();
            quill.formatText(0, quill.getLength(), {underline: false, bold: false}, 'silent')
            quill.formatText(0, quill.getLength(), {bold: false, underline: false}, 'silent') // @todo check this
            quill.update()
        },
        markIssues() {
            const quill = this.$refs.editor.getQuill();
            const regularIssueFormat = {underline: true}
            const activeIssueFormat = {bold: true}
            this.$store.getters.tips.forEach((el) => {
                let change = new Delta();
                change.retain(el.index);
                change.retain(el.size, el.active ? activeIssueFormat : regularIssueFormat);
                quill.updateContents(change);
            })
            quill.update()
        },
        async textChange(delta) {
            if (delta.source === 'user') {
                const quill = this.$refs.editor.getQuill()
                if (quill && quill.root) this.stripLinkTitles(quill.root)
                await this.refreshTextArea()
                const range = quill.getSelection()
                if (range && range.length === 0) {
                    let index = range.index
                    this.$store.getters.tips.forEach((el) => {
                        if (index >= el.index && index < el.index + el.size) {
                            this.$eventHub.emit('hoveringTextCursorOverSelectedText', el.id);
                        }
                    })
                }
                this.$store.dispatch('setIsTextInserted', quill.getLength() > 1)
                if (quill.getLength() <= 1) {
                    this.$store.dispatch('clearTips')
                }
            }
        },
        async refreshTextArea() {
            const quill = this.$refs.editor.getQuill()
            quill.update()
            this.dropIssuesMarks()
            await this.refreshTips()
            this.markIssues()
        },
        async refreshTips() {
            const quill = this.$refs.editor.getQuill()
            quill.update()
            const htmlText = this.getTextWithLinks(quill)
            // Используем тот же «чистый» текст, что и бэкенд после strip, чтобы индексы (cpIndex/cpSize)
            // совпадали при «Исправить» / «Исправить все» — иначе замены в строках со ссылками ломаются.
            const { text: plainText } = this.parseTextWithLinks(htmlText)
            await this.$store.dispatch('fetchTips', { htmlText, plainText });
            // Ждем обновления DOM перед расчетом bounds для корректной работы с эмодзи
            await this.$nextTick();
            setTimeout(() => {
                const quillLength = quill.getLength() - 1; // Quill добавляет \n в конец
                let rect = this.findQuillCoordinates()
                this.$store.dispatch('clearBounds')
                this.$store.getters.tips.forEach((el) => {
                    // Проверяем границы перед вызовом getBounds
                    const start = Math.max(0, Math.min(el.index, quillLength));
                    const end = Math.max(start, Math.min(el.index + el.size, quillLength));
                    const size = end - start;
                    
                    if (size > 0) {
                        let bounds = quill.getBounds(start, size)
                        if (bounds) {
                            bounds.inner_top = bounds.top
                            bounds.top += rect.top
                            bounds.left += rect.left
                            bounds.right = bounds.left + bounds.width
                            bounds.bottom = bounds.top + bounds.height
                            bounds.id = el.id
                            this.$store.dispatch('addBounds', bounds)
                        }
                    }
                })
            }, 50); // Небольшая задержка для рендеринга эмодзи
        },
        /**
         * Cmd/Ctrl+click по ссылке в редакторе — открыть в новой вкладке.
         * В contenteditable обычный клик не переходит по ссылке; модификатор даёт явное намерение.
         */
        handleEditorLinkClick(e) {
            if (e.target.tagName !== 'A' || !e.target.closest('.ql-editor')) return
            if (!(e.metaKey || e.ctrlKey)) return
            e.preventDefault()
            e.stopPropagation()
            const href = e.target.getAttribute('href')
            if (href && href !== 'about:blank') {
                window.open(href, '_blank', 'noopener,noreferrer')
            }
        },
        async copyTextToClipboard() {
            const quill = this.$refs.editor.getQuill()
            const plainText = quill.getText()
            const rawHtml = quill.root ? quill.root.innerHTML : ''
            const baseUrl = typeof window !== 'undefined' && window.location ? window.location.origin + '/' : ''
            const normalizedHtml = normalizeClipboardHtml(rawHtml, baseUrl)

            console.log('[clipboard] plain text preview:', JSON.stringify(plainText.slice(0, 120)))
            console.log('[clipboard] normalized HTML preview:', normalizedHtml.slice(0, 400))

            if (normalizedHtml) {
                const ok = await copyHtmlToClipboard(normalizedHtml)
                if (ok) {
                    await verifyCurrentClipboardFormats()
                    return
                }
                console.warn('[clipboard] copyHtmlToClipboard failed, writeText fallback')
            }

            try {
                await navigator.clipboard.writeText(plainText)
                console.log('[clipboard] SUCCESS via writeText (plain only)')
            } catch {
                console.warn('[clipboard] all methods failed')
            }
        }
    }
}
</script>

<style>
.ql-container {
    padding: 15px 70px 15px 5px;
    font-size: 1rem;
}
.ql-editor {
    padding: 0;
}

.ql-editor > p,
.ql-editor.ql-blank::before {
    top: 14px;
    left: 5px;
    margin: 0;
    padding: 0;
    font-size: 1rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.33;
    font-family: 'P22UndergroundCYBook', Helvetica Neue, Arial, sans-serif;
}

@media only screen and (max-width: 767px) {
    .ql-container {
        padding: 13px 15px 15px 5px;
        font-size: 1.1rem;
        max-height: 66.67vh;
    }
    .ql-editor > p,
    .ql-editor.ql-blank::before {
        top: 14px;
        left: 5px;
        font-size: 1.1rem;
    }
}

.ql-editor.ql-blank::before {
    color: rgba(0,0,0,0.3);
}
.ql-blank {
    margin: 0;
    padding: 0;
    tab-size: 0;
    white-space: inherit;
}
.ql-blank::before {
    margin: 0;
}
.ql-editor u {
    text-decoration: none;
    background-color: #fffada;
}
.ql-editor b,
.ql-editor strong {
    color: #000;
    background-color: #ffee81;
    font-weight: normal;
}
.ql-tooltip {
    display: none;
}

/* Стили ссылок как в шапке и футере: чёрные, border-bottom вместо underline */
#editor .ql-editor a,
.ql-editor a {
    color: #000;
    text-decoration: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.16);
}
#editor .ql-editor a:visited,
.ql-editor a:visited {
    color: #000;
}
#editor .ql-editor a:hover,
.ql-editor a:hover {
    color: #000;
}

/* Убираем балун с адресом (::before) и стрелку (::after) темы bubble */
.ql-container.ql-bubble .ql-editor a::before,
.ql-container.ql-bubble .ql-editor a::after {
    content: none !important;
    display: none;
    pointer-events: none;
}
</style>
